/* eslint-disable max-lines */

import { Dir } from '@angular/cdk/bidi';
import {
  OriginConnectionPosition,
  Overlay,
  OverlayConnectionPosition,
  OverlayRef,
  OverlayConfig,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import {
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  SimpleChange,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

import { BehaviorSubject, merge, of, pipe, Subject, Subscription } from 'rxjs';
import {
  delay,
  filter,
  first,
  map,
  pairwise,
  skip,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { filterBoolean, TaskPriority, TaskScheduler, UnionToIntersection } from '@recruitee/common';
import { asViewRef } from '@recruitee/utils';

import { RtPositionBuilderService } from '../../core/overlay/position/position-builder.service';
import { OVERLAY_LEVEL } from '../../dialog/providers';
import { RtTooltipInvalidPositionError } from './tooltip-errors';
import { TooltipPosition, TooltipType, TooltipVisibility } from './tooltip-inner.component';
import { TooltipComponent, TooltipComponentInputKeys, TooltipProps } from './tooltip.component';

/** Time in ms to delay before changing the tooltip visibility to hidden */
export const TOUCHEND_HIDE_DELAY = 1500;
const TOOLTIP_OFFSET = 6;
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noopFn = () => {};

/**
 * Directive that attaches a material design tooltip to the host element. Animates the showing and
 * hiding of a tooltip provided position (defaults to below the element).
 *
 * https://material.google.com/components/tooltips.html
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[rtTooltip]',
  host: {
    '(longpress)': 'showSubject.next()',
    '(touchend)': 'onTouchEnd()',
    '(mouseenter)': 'showSubject.next()',
    '(mouseleave)': 'onMouseLeave()',
  },
  exportAs: 'rtTooltip',
  standalone: true,
})

// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class RtTooltip implements OnChanges, OnDestroy {
  /** The default delay in ms before showing the tooltip after show is called */
  @Input('rtTooltipShowDelay') public showDelay: number = 300;
  /** The default delay in ms before hiding the tooltip after hide is called */
  @Input('rtTooltipHideDelay') public hideDelay: number = 300;

  @Input('rtTooltipType') public type: TooltipType = 'default';
  @Input('rtTooltipMaxWidth') public maxWidth: number | null | undefined = 300;
  @Input('rtTooltipWordWrap') public wordWrap: boolean | 'pre-line' = true;
  @Input('rtTooltipHasBorder') public hasBorder: boolean = false;
  @Input('rtTooltipHasShadow') public hasShadow: boolean = false;
  @Input('rtTooltipHasNoArrow') public hasNoArrow: boolean = false;
  @Input('rtTooltipForceShow') public forceShow: boolean = false;
  @Input('rtTooltipHasTextAlignCenter') public hasTextAlignCenter: boolean = false;
  @Input('rtTooltip') public message?: string | null | false;
  @Input() public autoMaxWidth: boolean = false;
  @Input('rtTooltipTemplate') public templateRef: TemplateRef<null> | null = null;
  /** Allows the user to define the position of the tooltip relative to the parent element */
  @Input('rtTooltipPosition')
  public position: TooltipPosition = 'above';

  @Input('rtTooltipEnabled')
  public tooltipEnabled: boolean | (() => boolean) = true;

  @Input('rtTooltipSkipTransition') public skipTransition: boolean = false;

  public showSubject = new Subject<{ delay: number } | void>();
  public hideSubject = new BehaviorSubject({ eventEmitted: false, delay: this.hideDelay });

  private ngOnDestroy$ = new Subject<void>();
  private raftick: boolean = false;
  private show$ = this.showSubject.asObservable();
  private hideEvent$ = this.hideSubject.pipe(filter(event => event.eventEmitted));
  private hideEventDelayed$ = this.hideEvent$.pipe(
    switchMap(event => of(event).pipe(delay(event.delay))),
  );
  private tooltipProps = new BehaviorSubject<Partial<TooltipProps>>({
    templateRef: null,
    message: '',
    skipTransition: false,
  });
  private tooltipVisibility$ = new BehaviorSubject<TooltipVisibility | null>(null);
  private overlayLevel = inject(OVERLAY_LEVEL);
  private teardownFn: () => void = noopFn;

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private scrollDispatcher: ScrollDispatcher,
    private cdRef: ChangeDetectorRef,
    private taskScheduler: TaskScheduler,
    private rtOverlayPositionBuilder: RtPositionBuilderService,
    @Optional() private dir: Dir,
  ) {}

  public ngOnInit(): void {
    this.taskScheduler.scheduleMacrotask(TaskPriority.Low, () => {
      if (asViewRef(this.cdRef).destroyed) return;

      this.watchTooltipEvents();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const tooltipEnabled = this.isTooltipEnabled();

    if (changes.tooltipEnabled && !tooltipEnabled) {
      this.hideSubject.next({ ...this.hideSubject.value, eventEmitted: true });
    }

    if (changes.tooltipEnabled && this.forceShow && tooltipEnabled) {
      this.showSubject.next();
    }
    const tooltipPropsChanges = TooltipComponentInputKeys.reduce(
      (acc: Partial<TooltipProps>, key: keyof TooltipProps) => {
        if (key in changes) {
          return {
            ...acc,
            [key]: changes[key].currentValue,
          };
        }

        return acc;
      },
      {},
    );
    this.tooltipProps.next({
      ...this.tooltipProps.value,
      ...tooltipPropsChanges,
    });
  }

  /**
   * Dispose the tooltip when destroyed.
   */
  public ngOnDestroy(): void {
    this.hideSubject.next({ ...this.hideSubject.value, eventEmitted: true });
    this.teardownFn();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  // eslint-disable-next-line max-lines-per-function
  public watchTooltipEvents(): void {
    const resetHideEvent = () =>
      pipe(
        tap(() =>
          this.hideSubject.next({
            ...this.hideSubject.value,
            eventEmitted: false,
          }),
        ),
      );

    this.show$
      .pipe(
        resetHideEvent(),
        switchMap(() => of(null).pipe(delay(this.showDelay), takeUntil(this.hideEvent$))),
        filter(() => {
          const tooltipEnabled = this.isTooltipEnabled();
          const hasMessage = this.message && this.message.trim();
          return Boolean(tooltipEnabled && ((hasMessage && !this.templateRef) || this.templateRef));
        }),
        map(() => this.createTooltip()),
        tap(({ tooltipRef, overlayRef }) => {
          this.updateTooltipProps(tooltipRef, <TooltipProps>{}, this.tooltipProps.value, true);
          tooltipRef.instance.show(this.position);
          this.calculateAutoMaxWidth(overlayRef, tooltipRef);
          this.stabilizeOverlayPosition(overlayRef);
          this.watchPropsEmptyMessage();
        }),
        switchMap(({ tooltipRef, overlayRef }) => {
          const tooltipInstance = tooltipRef.instance;
          const scrollSubscription = this.attachScroll(overlayRef);
          this.teardownFn = () => {
            scrollSubscription.unsubscribe();
            this.disposeTooltip(overlayRef);
            this.teardownFn = noopFn;
          };

          return merge(
            this.publishVisibilityState$(tooltipInstance),
            this.updateTooltipProps$(tooltipRef),
            this.watchHideEvents$(tooltipInstance).pipe(resetHideEvent()),
          );
        }),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe();
  }

  public onMouseLeave(): void {
    this.hideSubject.next({ delay: this.hideDelay, eventEmitted: true });
  }

  public onTouchEnd(): void {
    this.hideSubject.next({ delay: TOUCHEND_HIDE_DELAY, eventEmitted: true });
  }

  private updateTooltipProps$(tooltipRef: ComponentRef<TooltipComponent>) {
    return this.tooltipProps.pipe(
      pairwise(),
      tap(([previousState, currentState]) =>
        this.updateTooltipProps(tooltipRef, previousState, currentState),
      ),
      takeUntil(this.hideEvent$),
    );
  }

  private publishVisibilityState$(tooltipInstance: TooltipComponent) {
    return tooltipInstance.visibilityChange.pipe(
      tap(visibility => this.tooltipVisibility$.next(visibility)),
      takeUntil(this.hideEvent$),
    );
  }

  private animationEvent$(tooltipInstance: TooltipComponent, name: TooltipVisibility) {
    return tooltipInstance.animationState.pipe(
      filterBoolean,
      filter(animationState => animationState.toState === name),
    );
  }

  private watchHideEvents$(tooltipInstance: TooltipComponent) {
    return this.animationEvent$(tooltipInstance, 'visible').pipe(
      switchMap(() =>
        merge(
          this.hideEventDelayed$,
          tooltipInstance.visibilityChange.pipe(filter(visibility => visibility === 'hidden')),
        ).pipe(first()),
      ),
      tap(() => this.hide(tooltipInstance)),
      switchMap(() => this.animationEvent$(tooltipInstance, 'hidden')),
      tap(() => this.teardownFn()),
    );
  }

  private watchPropsEmptyMessage(): void {
    this.tooltipProps
      .pipe(
        takeUntil(this.hideEvent$ || this.ngOnDestroy$),
        map(({ templateRef, message }) => !(templateRef && message)),
        filterBoolean,
        skip(1),
      )
      .subscribe(() => {
        this.hideSubject.next({ delay: 0, eventEmitted: true });
      });
  }

  private attachScroll(overlayRef: OverlayRef): Subscription {
    return this.scrollDispatcher.scrolled(0).subscribe(() => {
      if (!this.raftick) {
        this.raftick = true;
        requestAnimationFrame(() => {
          if (overlayRef) {
            overlayRef.updatePosition();
          }
          this.raftick = false;
        });
      }
    });
  }

  /** Shows the tooltip after the delay in ms, defaults to tooltip-delay-show or 0ms if no input */
  private updateTooltipProps(
    tooltipRef: ComponentRef<TooltipComponent>,
    previousState: Partial<TooltipProps>,
    currentState: Partial<TooltipProps>,
    isFirstChange = false,
  ): void {
    const tooltipInstance = tooltipRef.instance;
    const simpleChanges: Partial<{ [K in keyof TooltipProps]: SimpleChange }> = {};
    Object.entries(currentState).forEach(
      ([key, value]: [
        keyof TooltipProps,
        UnionToIntersection<TooltipComponent[keyof TooltipProps]>,
      ]) => {
        if (previousState[key] !== value) {
          simpleChanges[key] = new SimpleChange(previousState[key], value, isFirstChange);
          tooltipInstance[key] = value;
        }
      },
    );
    tooltipInstance.ngOnChanges(<SimpleChanges>simpleChanges);
    tooltipRef.changeDetectorRef.detectChanges();
  }

  /** Hides the tooltip after the delay in ms, defaults to tooltip-delay-hide or 0ms if no input */
  private hide(tooltipInstance: TooltipComponent): void {
    tooltipInstance.hide();
    this.cdRef.detectChanges();
  }

  /** Shows/hides the tooltip */
  private toggle(): void {
    this.isTooltipVisible()
      ? this.hideSubject.next({ ...this.hideSubject.value, eventEmitted: true })
      : this.showSubject.next();
  }

  /** Returns true if the tooltip is currently visible to the user */
  private isTooltipVisible(): boolean {
    return this.tooltipVisibility$.value === 'visible';
  }

  private stabilizeOverlayPosition(overlayRef: OverlayRef): void {
    const MAX_ATTEMPTS = 5;
    const getOverlayPosition = () => ({
      top: overlayRef.overlayElement.style.top,
      left: overlayRef.overlayElement.style.left,
    });
    let position = getOverlayPosition();
    let isPositionStable = false;
    let updateCount = 0;
    while (!isPositionStable && updateCount < MAX_ATTEMPTS) {
      overlayRef.updatePosition();
      const currentPosition = getOverlayPosition();
      updateCount++;
      isPositionStable =
        currentPosition.left === position.left && currentPosition.top === position.top;
      position = currentPosition;
    }
  }

  private isTooltipEnabled(): boolean {
    return (
      this.tooltipEnabled === null ||
      this.tooltipEnabled === undefined ||
      (typeof this.tooltipEnabled === 'boolean' && this.tooltipEnabled) ||
      (typeof this.tooltipEnabled === 'function' && this.tooltipEnabled())
    );
  }

  /** Create the tooltip to display */
  private createTooltip(): { tooltipRef: ComponentRef<TooltipComponent>; overlayRef: OverlayRef } {
    const overlayRef = this.createOverlay();
    const portal = new ComponentPortal(TooltipComponent, this.viewContainerRef);
    const tooltipRef = overlayRef.attach(portal);
    tooltipRef.changeDetectorRef.detectChanges();

    return { tooltipRef, overlayRef };
  }

  /** Create the overlay config and position strategy */
  private createOverlay(): OverlayRef {
    const origin = this.getOrigin();
    const position = this.getOverlayPosition();
    const strategy = this.rtOverlayPositionBuilder
      .rtFlexibleConnectedTo(this.elementRef)
      .withPositions([{ ...origin, ...position }])
      .withOverlayLevel(this.overlayLevel);
    const tooltipPosition = this.position;
    const isLtr = !this.dir || this.dir.value === 'ltr';

    if (tooltipPosition === 'left' || (isLtr && tooltipPosition === 'after'))
      strategy.withDefaultOffsetX(TOOLTIP_OFFSET * -1);
    if (
      tooltipPosition === 'right' ||
      (isLtr && tooltipPosition === 'before') ||
      tooltipPosition === 'righttop'
    )
      strategy.withDefaultOffsetX(TOOLTIP_OFFSET);
    if (tooltipPosition === 'above' || tooltipPosition === 'aboveright')
      strategy.withDefaultOffsetY(TOOLTIP_OFFSET * -1);
    if (tooltipPosition === 'below') strategy.withDefaultOffsetY(TOOLTIP_OFFSET);

    const config = new OverlayConfig();
    config.positionStrategy = strategy;

    return this.overlay.create(config);
  }

  /** Disposes the current tooltip and the overlay it is attached to */
  private disposeTooltip(overlayRef: OverlayRef): void {
    overlayRef.dispose();
  }

  /** Returns the origin position based on the user's position preference */
  private getOrigin(): OriginConnectionPosition {
    if (this.position === 'above' || this.position === 'below') {
      return { originX: 'center', originY: this.position === 'above' ? 'top' : 'bottom' };
    }

    if (this.position === 'aboveright') {
      return { originX: 'end', originY: 'top' };
    }

    if (this.position === 'righttop') {
      return { originX: 'end', originY: 'top' };
    }

    if (this.position === 'leftbelow') {
      return { originX: 'start', originY: 'bottom' };
    }

    const isDirectionLtr = !this.dir || this.dir.value === 'ltr';
    if (
      this.position === 'left' ||
      (this.position === 'before' && isDirectionLtr) ||
      (this.position === 'after' && !isDirectionLtr)
    ) {
      return { originX: 'start', originY: 'center' };
    }

    if (
      this.position === 'right' ||
      (this.position === 'after' && isDirectionLtr) ||
      (this.position === 'before' && !isDirectionLtr)
    ) {
      return { originX: 'end', originY: 'center' };
    }

    throw new RtTooltipInvalidPositionError(this.position);
  }

  /** Returns the overlay position based on the user's preference */
  private getOverlayPosition(): OverlayConnectionPosition {
    if (this.position === 'above') {
      return { overlayX: 'center', overlayY: 'bottom' };
    }

    if (this.position === 'below') {
      return { overlayX: 'center', overlayY: 'top' };
    }

    if (this.position === 'aboveright') {
      return { overlayX: 'end', overlayY: 'bottom' };
    }

    if (this.position === 'righttop') {
      return { overlayX: 'start', overlayY: 'top' };
    }

    if (this.position === 'leftbelow') {
      return { overlayX: 'start', overlayY: 'top' };
    }

    const isLtr = !this.dir || this.dir.value === 'ltr';
    if (
      this.position === 'left' ||
      (this.position === 'before' && isLtr) ||
      (this.position === 'after' && !isLtr)
    ) {
      return { overlayX: 'end', overlayY: 'center' };
    }

    if (
      this.position === 'right' ||
      (this.position === 'after' && isLtr) ||
      (this.position === 'before' && !isLtr)
    ) {
      return { overlayX: 'start', overlayY: 'center' };
    }

    throw new RtTooltipInvalidPositionError(this.position);
  }

  /** if flag is true, the maximum width will be calculated to predict dialog
   *  left overflow  */
  private calculateAutoMaxWidth(
    overlayRef: OverlayRef,
    tooltipRef: ComponentRef<TooltipComponent>,
  ): void {
    if (!this.autoMaxWidth) {
      return;
    }

    const { offsetLeft, offsetWidth } = overlayRef.overlayElement;
    const width = offsetWidth / 2;
    if (width > offsetLeft) {
      const newWidth = offsetLeft * 2;
      this.updateTooltipProps(tooltipRef, tooltipRef.instance, {
        ...tooltipRef.instance,
        maxWidth: newWidth,
      });
      this.tooltipProps.next({
        ...this.tooltipProps.value,
        maxWidth: newWidth,
      });
      tooltipRef.instance.cdRef.detectChanges();
      this.cdRef.detectChanges();
    }
  }
}
